import { useEffect, useState } from "react";
import { Calculator, CheckCircle2, Send } from "lucide-react";
import { api, apiError } from "../../lib/api";
import { formatPhone, isValidEmail, isValidPhone } from "../../data/options";
import { Field } from "./FormBits";
import { ConsentBoxes } from "./ConsultationSection";

const INPUTS = [
  ["employment_income", "Revenu d'emploi"], ["self_employment_income", "Revenu de travail autonome (net)"],
  ["other_income", "Autres revenus (intérêts, location, etc.)"], ["capital_gains", "Gains en capital"],
  ["eligible_dividends", "Dividendes déterminés"], ["non_eligible_dividends", "Dividendes non déterminés"],
  ["rrsp_deduction", "Cotisation REER déduite"],
];
const money = (v) => (v ?? 0).toLocaleString("fr-CA", { style: "currency", currency: "CAD" });
const toNum = (s) => Math.max(0, Number(String(s).replace(/\s/g, "").replace(",", ".")) || 0);

const Results = ({ r }) => (
  <div className="rounded-2xl bg-[#650D23] p-6 text-[#FFFDFB] sm:p-8" data-testid="calc-results">
    <p className="eyebrow !text-[#EDE3D9]">Estimation {r.year} — Québec</p>
    <div className="mt-5 grid grid-cols-2 gap-5">
      {[["Revenu total", r.total_income, "calc-total-income"], ["Revenu imposable", r.taxable_income, "calc-taxable"], ["Impôt fédéral", r.federal_tax, "calc-federal"],
        ["Impôt du Québec", r.quebec_tax, "calc-quebec"], ["Impôt total", r.total_tax, "calc-total-tax"], ["Revenu après impôt", r.after_tax_income, "calc-after-tax"]].map(([l, v, id]) => (
        <div key={id}><p className="text-xs text-[#EDE3D9]">{l}</p><p className="font-display text-xl sm:text-2xl" data-testid={id}>{money(v)}</p></div>
      ))}
      <div><p className="text-xs text-[#EDE3D9]">Taux moyen</p><p className="font-display text-2xl" data-testid="calc-average">{r.average_rate.toLocaleString("fr-CA")} %</p></div>
      <div><p className="text-xs text-[#EDE3D9]">Taux marginal</p><p className="font-display text-2xl" data-testid="calc-marginal">{r.marginal_rate.toLocaleString("fr-CA")} %</p></div>
    </div>
  </div>
);

export const SendResultsForm = ({ endpoint, payload, prefix }) => {
  const [f, setF] = useState({ full_name: "", email: "", phone: "", message: "", consent_data: false, consent_marketing: false, website: "" });
  const [err, setErr] = useState({});
  const [state, setState] = useState({ busy: false, done: false, error: "" });
  const set = (k) => (v) => setF((p) => ({ ...p, [k]: v }));
  const submit = async (e) => {
    e.preventDefault();
    const x = {};
    if (f.full_name.trim().length < 2) x.full_name = "Veuillez indiquer votre nom.";
    if (!isValidEmail(f.email)) x.email = "Adresse courriel invalide.";
    if (!isValidPhone(f.phone)) x.phone = "Numéro canadien à 10 chiffres requis.";
    if (!f.consent_data) x.consent_data = "Votre consentement est requis.";
    setErr(x);
    if (Object.keys(x).length) return;
    setState({ busy: true, done: false, error: "" });
    try { await api.post(endpoint, { ...f, ...payload }); setState({ busy: false, done: true, error: "" }); } catch (e2) { setState({ busy: false, done: false, error: apiError(e2) }); }
  };
  const t = (s) => `${prefix}-send-${s}`;
  if (state.done) return <p className="flex items-center gap-2 rounded-2xl bg-[#FAF6F1] p-5 text-[#650D23]" data-testid={t("success")}><CheckCircle2 className="h-5 w-5" />Vos résultats ont été transmis à STRUKTIV. Un conseiller pourra vous contacter.</p>;
  return (
    <form onSubmit={submit} noValidate className="space-y-4 rounded-2xl border border-[#EDE3D9] bg-[#FFFDFB] p-6 text-left text-[#242020]" data-testid={t("form")}>
      <h3 className="text-xl">Envoyer mes résultats à STRUKTIV</h3>
      <input type="text" tabIndex={-1} autoComplete="off" value={f.website} onChange={(e) => set("website")(e.target.value)} className="hidden" aria-hidden="true" />
      <Field label="Nom complet" required error={err.full_name} htmlFor={t("name")}><input id={t("name")} className="field-input" value={f.full_name} onChange={(e) => set("full_name")(e.target.value)} data-testid={t("name")} /></Field>
      <div className="grid gap-4 sm:grid-cols-2">
        <Field label="Courriel" required error={err.email} htmlFor={t("email")}><input id={t("email")} type="email" className="field-input" value={f.email} onChange={(e) => set("email")(e.target.value)} data-testid={t("email")} /></Field>
        <Field label="Téléphone" required error={err.phone} htmlFor={t("phone")}><input id={t("phone")} type="tel" placeholder="(514) 123-4567" className="field-input" value={f.phone} onChange={(e) => set("phone")(formatPhone(e.target.value))} data-testid={t("phone")} /></Field>
      </div>
      <Field label="Message (facultatif)" htmlFor={t("msg")}><textarea id={t("msg")} rows={2} className="field-input" value={f.message} onChange={(e) => set("message")(e.target.value)} data-testid={t("message")} /></Field>
      <ConsentBoxes f={f} set={set} err={err} prefix={prefix} />
      {state.error && <p className="text-sm text-[#b42318]" role="alert" data-testid={t("error")}>{state.error}</p>}
      <button className="btn-primary w-full" disabled={state.busy} data-testid={t("button")}><Send className="h-4 w-4" />{state.busy ? "Envoi…" : "Envoyer à finco@struktiv.info"}</button>
    </form>
  );
};

export const TaxCalculator = () => {
  const [vals, setVals] = useState(Object.fromEntries(INPUTS.map(([k]) => [k, ""])));
  const [res, setRes] = useState(null);
  const inputs = Object.fromEntries(INPUTS.map(([k]) => [k, toNum(vals[k])]));
  const key = JSON.stringify(inputs);
  useEffect(() => {
    const t = setTimeout(() => { api.post("/calculator/compute", JSON.parse(key)).then((r) => setRes(r.data)).catch(() => {}); }, 300);
    return () => clearTimeout(t);
  }, [key]);

  return (
    <section id="calculatrice" className="rounded-[2rem] border border-[#EDE3D9] bg-[#FAF6F1]/95 p-6 sm:p-10" data-testid="tax-calculator">
      <div className="flex items-center gap-3"><span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-[#650D23] text-[#FFFDFB]"><Calculator className="h-5 w-5" /></span>
        <div><p className="eyebrow">Apprendre — Outil</p><h2 className="text-3xl">Calculatrice d'impôt 2026 (Québec)</h2></div></div>
      <p className="mt-4 max-w-3xl text-[#242020]/75">Estimez votre impôt fédéral et provincial, votre taux moyen et votre taux marginal selon vos revenus de l'année.</p>
      <div className="mt-8 grid gap-8 lg:grid-cols-2">
        <div className="grid content-start gap-4 self-start sm:grid-cols-2">
          {INPUTS.map(([k, l]) => (
            <Field key={k} label={l} htmlFor={`calc-${k}`}>
              <div className="relative"><input id={`calc-${k}`} inputMode="decimal" className="field-input !pr-8" placeholder="0" value={vals[k]}
                onChange={(e) => setVals((p) => ({ ...p, [k]: e.target.value.replace(/[^\d\s.,]/g, "") }))} data-testid={`calc-input-${k}`} />
                <span className="absolute right-3 top-3 text-sm text-[#242020]/50">$</span></div>
            </Field>
          ))}
        </div>
        <div className="space-y-6">{res && <Results r={res} />}<SendResultsForm endpoint="/calculator/send" payload={{ inputs }} prefix="calc" /></div>
      </div>
      <p className="mt-6 text-xs leading-relaxed text-[#242020]/60" data-testid="calc-disclaimer">Estimation simplifiée à titre informatif, basée sur les taux et paliers 2026 et les montants personnels de base. Elle n'inclut pas les cotisations (RRQ, RQAP, AE), l'impôt minimum de remplacement ni les autres crédits et déductions. Elle ne constitue pas un conseil fiscal : validez votre situation avec un professionnel.</p>
    </section>
  );
};
